import React, { useState, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { JVInput } from "../../../../../shared/JVInput";
import { Button } from "primereact/button";
import { JVDropdown } from "../../../../../shared/JVDropdown";
import { IBranchMastDropdown, ICompanyMastDropdown, ITeamsDropdown } from "../../../../../models/IDropdown";
import { Fetch } from "../../../../../shared/Fetch";
import { MultiSelect } from "primereact/multiselect";
import { DataTable } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { safeFormatDate } from "../../../../../models/DateFormatMapper";
import { ILogin } from "../../../../../models/Login";

interface Props {
  reload: boolean;
  setReload: (reload: boolean) => void;
  toast: any;
}

interface DiceUserSettingFormValues {
  Team_Name: number | string;
  Company: number | string;
  No_Of_Views_Allowed: number | string;
}

interface IDiceTeamSetting {
  id?: number;
  team_ID: number;
  team_Name?: string;
  company_Name?: string;
  branch_Name?: string;
  no_Of_Views: number;
  updated_By?: number;
  updated_At?: string;
}

const schema = yup.object({
  Company: yup.number().typeError("Company is required").required("Company is required"),
  Team_Name: yup.number().typeError("Team Name is required").required("Team Name is required"),
  No_Of_Views_Allowed: yup.number().typeError("Must be a number").positive("Must be greater than 0").integer("Must be a whole number").required("Number of Views Allowed is required"),
});

const DiceTeamSettingForm = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableSaveLoading, setTableSaveLoading] = useState<boolean>(false);
  const [company, setCompany] = useState<Array<ICompanyMastDropdown>>([]);
  const [branch, setBranch] = useState<Array<IBranchMastDropdown>>([]);
  const [team, setTeam] = useState<Array<ITeamsDropdown>>([]);
  const [selectedBranch, setSelectedBranch] = useState<Array<number>>([]);
  const [tableData, setTableData] = useState<Array<IDiceTeamSetting>>([]);
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const [editedIds, setEditedIds] = useState<Set<number>>(new Set());
  const dt = useRef<any>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DiceUserSettingFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: { Team_Name: "", Company: "", No_Of_Views_Allowed: "" },
    shouldFocusError: false,
  });

  const selectedCompany = useWatch({ control, name: "Company" });
  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  const DateFormat = loginObj.date_Format;
  // ── Dropdowns ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadCompany = async () => {
      const response = await Fetch("Dice_Setting/getCompany", "GET", null, null, null);
      if (response) {
        const data: Array<ICompanyMastDropdown> = await response.json();
        data.unshift({ company_ID: 0, company_Name: "-- SELECT --" });
        setCompany(data);
      }
    };
    const loadTeam = async () => {
      const response = await Fetch("Dice_Setting/getTeams", "GET", null, null, null);
      if (response) {
        const data: Array<ITeamsDropdown> = await response.json();
        data.unshift({ team_ID: 0, team_Name: "-- SELECT --" });
        setTeam(data);
      }
    };
    loadCompany();
    loadTeam();
  }, []);

  useEffect(() => {
    if (!selectedCompany || selectedCompany === 0 || selectedCompany === "") {
      setBranch([]);
      setTeam([]);
      setSelectedBranch([]);
      return;
    }
    const loadBranch = async () => {
      const response = await Fetch("Dice_Setting/getBranch", "GET", null, `company_id=${selectedCompany}`, null);
      if (response) {
        const data: Array<IBranchMastDropdown> = await response.json();
        data.unshift({ branch_ID: 0, branchName: "-- SELECT --", company_ID: 0 });
        setBranch(data);
        setSelectedBranch([]);
      }
    };
    const loadTeam = async () => {
      const response = await Fetch("Dice_Setting/getTeams", "GET", null, `company_ID=${selectedCompany}`, null);
      if (response) {
        const data: Array<ITeamsDropdown> = await response.json();
        data.unshift({ team_ID: 0, team_Name: "-- SELECT --" });
        setTeam(data);
      }
    };
    loadBranch();
    loadTeam();
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedBranch.length > 0) {
      const loadTeamByBranch = async () => {
        //const queryParameter = selectedBranch.map((b) => `branch_ID=${b}`).join("&");
        const response = await Fetch("Dice_Setting/getTeams", "GET", null, `branch_IDs=${selectedBranch.join(",")}`, null);
        if (response) {
          const data: Array<ITeamsDropdown> = await response.json();
          data.unshift({ team_ID: 0, team_Name: "-- SELECT --" });
          setTeam(data);
        }
      };
      loadTeamByBranch();
    }
  }, [selectedBranch]);

  // ── Table load ────────────────────────────────────────────────────────────
  const loadTableData = async () => {
    setTableLoading(true);
    const response = await Fetch("Dice_Setting/getDiceTeamSetting", "GET", null, null, props.toast);
    if (response) {
      const data: Array<IDiceTeamSetting> = await response.json();
      // console.log("TABLE DATA FROM API:", data);
      setTableData(data);
    }
    setTableLoading(false);
  };

  useEffect(() => {
    loadTableData();
  }, [props.reload]);

  // ── Form submit ───────────────────────────────────────────────────────────
  const onSubmit = async (data: DiceUserSettingFormValues) => {
    try {
      setLoading(true);
      const payload = {
        Team_ID: data.Team_Name,
        Company_ID: data.Company,
        No_Of_Views: data.No_Of_Views_Allowed,
        Branch: selectedBranch.length > 0 ? selectedBranch : null,
      };
      const resp = await Fetch("Dice_Setting/addDiceTeamSetting", "POST", payload, null, props.toast);
      if (resp) {
        props.toast?.current?.show({ severity: "success", detail: "Added Successfully", life: 3000 });
        props.setReload(!props.reload);
        reset();
        setSelectedBranch([]);
      }
    } catch {
      props.toast?.current?.show({ severity: "error", detail: "Save failed", life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // ── Table save ────────────────────────────────────────────────────────────
  const onTableSave = async () => {
    if (editedIds.size === 0) {
      props.toast.current.show({ severity: "warn", detail: "No changes to save.", life: 3000 });
      return;
    }

    setTableSaveLoading(true);
    const payload = tableData
      .filter((el) => editedIds.has(el.id!))
      .map((el) => ({
        ID: el.id,
        No_Of_Views: el.no_Of_Views,
      }));

    const resp = await Fetch("Dice_Setting/updateDiceTeamSetting", "PUT", payload, null, props.toast);
    if (resp) {
      props.toast.current.show({ severity: "success", detail: "Updated Successfully", life: 3000 });
      props.setReload(!props.reload);
      setFocusIndex(-1);
      setEditedIds(new Set());
    }
    setTableSaveLoading(false);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isPositiveInteger = (val: any) => {
    let str = String(val).trim();
    if (!str) return false;
    str = str.replace(/^0+/, "") || "0";
    const n = Math.floor(Number(str));
    return n !== Infinity && String(n) === str && n >= 0;
  };

  const onCellEditComplete = (e: any) => {
    const { rowData, newValue, field, originalEvent: event } = e;

    if (field === "no_Of_Views") {
      if (isPositiveInteger(newValue)) {
        rowData[field] = newValue;

        setEditedIds((prev) => new Set(prev).add(rowData.id));
      } else {
        event.preventDefault();
      }
    } else {
      if (newValue?.toString().trim().length > 0) {
        rowData[field] = newValue;
        setEditedIds((prev) => new Set(prev).add(rowData.id));
      } else {
        event.preventDefault();
      }
    }

    setTableData((prev) =>
      prev.map((item) => {
        if (item.team_ID === rowData.team_ID) {
          return {
            ...item,
            no_Of_Views: isPositiveInteger(rowData.no_Of_Views) ? Number(rowData.no_Of_Views) : item.no_Of_Views,
          };
        }
        return item;
      }),
    );
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (options.editorCallback) options.editorCallback(e.target.value);
    };
    return <input style={{ width: "100%", margin: 0, padding: "3px 5px", border: "2px solid green", cursor: "pointer" }} value={options.value} type="number" onChange={handleChange} />;
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="field flex align-items-end gap-2">
          <JVDropdown column={6} label="Company Name" control={control} name="Company" options={company} optionLabel="company_Name" optionValue="company_ID" placeholder="Select Company Name" error={errors.Company?.message} />
          <div className="field col-12 md:col-3">
            <p>Branch</p>
            <MultiSelect
              style={{ height: "37px", marginTop: "-10px" }}
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.value)}
              options={branch}
              optionValue="branch_ID"
              optionLabel="branchName"
              display="chip"
              filter
              placeholder="Select Branches"
              disabled={!selectedCompany || selectedCompany === 0}
            />
          </div>
          <JVDropdown column={6} label="Team Name" control={control} name="Team_Name" options={team} optionLabel="team_Name" optionValue="team_ID" placeholder="Select Team Name" disabled={!selectedCompany || selectedCompany === 0} error={errors.Team_Name?.message} />
          <JVInput required type="number" column={4} label="Number of Views Allowed" control={control} name="No_Of_Views_Allowed" error={errors.No_Of_Views_Allowed?.message} />
          <div className=" flex align-items-end  field col-12 md:col-3">
            <Button loading={loading} disabled={loading} style={{ width: "100px" }} label="ADD" type="submit" />
          </div>
        </div>
      </form>

      <DataTable style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0", marginTop: "1rem" }} size="small" ref={dt} value={tableData} emptyMessage="No Data found." loading={tableLoading} showGridlines stripedRows responsiveLayout="scroll">
        <Column field="company_Name" header="Company" />
        <Column field="branch_Name" header="Branch" />
        <Column field="team_Name" header="Team" />
        <Column
          field="no_Of_Views"
          header="No of Views Allowed"
          body={(rowData: IDiceTeamSetting, options: any) => (
            <p className={options.rowIndex === focusIndex ? "glow-loop" : ""} style={{ color: options.rowIndex === focusIndex ? "red" : "black" }}>
              {rowData.no_Of_Views}
            </p>
          )}
          editor={(options) => cellEditor(options)}
          onCellEditComplete={onCellEditComplete}
        />
        <Column header="Created At" body={(rowData: any) => safeFormatDate(rowData.created_At, "dd/MM/yyyy", DateFormat, true)} />
        <Column field="createdBy" header="Created By" />
        <Column field="updatedBy" header="Updated By" />
      </DataTable>

      <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
        <Button loading={tableSaveLoading} disabled={tableSaveLoading || tableData.length === 0} style={{ width: "100px" }} label="SAVE" type="button" onClick={onTableSave} />
      </div>
    </>
  );
};

export default DiceTeamSettingForm;
